import { MutationOptions, useMutation } from "react-query";
import { API_URL, APIError, APIResponse, post } from "../api";
import type { CaseTicketPriority } from "./useCaseTickets";

interface CreateCaseTicketPayload {
  category: string;
  subject: string;
  description?: string;
  priority?: CaseTicketPriority;
  subcategory?: string;
  channel?: string;
}

interface AddCaseTicketMessagePayload {
  ticketId: string;
  message: string;
}

interface CreateCaseDisputePayload {
  ticketId?: string;
  transactionId?: string;
  amount?: number;
  currency?: string;
  reasonCode: string;
  reasonText?: string;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export function useCreateCaseTicket(
  options?: MutationOptions<APIResponse<any>, APIError, CreateCaseTicketPayload>
) {
  const { isLoading, isError, error, mutate, mutateAsync } = useMutation<
    APIResponse<any>,
    APIError,
    CreateCaseTicketPayload
  >(
    (payload) =>
      post(API_URL + "/case-management/tickets", payload, {
        headers: authHeaders(),
      }),
    { ...options }
  );

  return {
    createCaseTicket: mutate,
    createCaseTicketAsync: mutateAsync,
    isCreatingTicket: isLoading,
    isCreateTicketError: isError,
    createTicketError: error,
  };
}

export function useAddCaseTicketMessage(
  options?: MutationOptions<APIResponse<any>, APIError, AddCaseTicketMessagePayload>
) {
  const { isLoading, isError, error, mutate, mutateAsync } = useMutation<
    APIResponse<any>,
    APIError,
    AddCaseTicketMessagePayload
  >(
    ({ ticketId, message }) =>
      post(
        API_URL + `/case-management/tickets/${ticketId}/messages`,
        { message, visibility: "PUBLIC" },
        { headers: authHeaders() }
      ),
    { ...options }
  );

  return {
    addCaseTicketMessage: mutate,
    addCaseTicketMessageAsync: mutateAsync,
    isSendingMessage: isLoading,
    isSendMessageError: isError,
    sendMessageError: error,
  };
}

export function useCreateCaseDispute(
  options?: MutationOptions<APIResponse<any>, APIError, CreateCaseDisputePayload>
) {
  const { isLoading, isError, error, mutate, mutateAsync } = useMutation<
    APIResponse<any>,
    APIError,
    CreateCaseDisputePayload
  >(
    (payload) =>
      post(API_URL + "/case-management/disputes", payload, {
        headers: authHeaders(),
      }),
    { ...options }
  );

  return {
    createCaseDispute: mutate,
    createCaseDisputeAsync: mutateAsync,
    isCreatingDispute: isLoading,
    isCreateDisputeError: isError,
    createDisputeError: error,
  };
}
